'use client';

import React, { useCallback, useMemo, forwardRef, useImperativeHandle, type ForwardedRef } from 'react';
import { observer } from '@legendapp/state/react';
import {
    type AppBottomSheetInjectedProps,
    withAppBottomSheet,
    type AppBottomSheetHeaderProps,
} from '@kidslabs/mario-nextjs-react-ui-core';
import type { GiftsSelectorWidgetProps, GiftsSelectorWidgetRef } from './gifts-selector-widget.types';
import { useProductGiftHook } from '../../hooks/product-gift.hook';
import { useGiftSelection } from '../../hooks/use-gift-selection.hook';
import { useCartItemGiftSelection } from '../../hooks/use-cart-item-gift-selection.hook';
import { cartItemReferenceStoreAPI } from '@kidslabs/module-quote';
import { giftSelectionStoreAPI } from '../../stores/simple-gift-selection-store.public-api';
import { GiftsSelectorWidgetContents } from '../gifts-selector-widget-contents/gifts-selector-widget-contents/gifts-selector-widget-contents';
import type { ProductModel as GiftProductModel } from '../gifts-selector-widget-contents/gifts-selector-widget-contents.types';
import { GiftSelectorBottomSheetFooter } from '../gift-selector-bottom-sheet-footer/gift-selector-bottom-sheet-footer';
import styles from './gifts-selector-widget.module.css';
import { useTranslation } from 'next-i18next';
/**
 * Memoized footer component to prevent unnecessary re-renders
 */
const MemoizedFooter = React.memo<{
    onBottomSheetClose: () => void;
    onConfirm: () => void;
}>(({ onBottomSheetClose, onConfirm }) => {
    return <GiftSelectorBottomSheetFooter onBottomSheetClose={onBottomSheetClose} onConfirm={onConfirm} />;
});

MemoizedFooter.displayName = 'MemoizedFooter';

/**
 * Memoized content component to prevent unnecessary re-renders
 */
const MemoizedContent = React.memo<{
    product: GiftProductModel;
    productQuantity?: number;
    cartItemReference?: GiftsSelectorWidgetProps['cartItemReference'];
    enableAutoFill?: boolean;
    onAutoFillQuantity?: (newQuantity: number) => void;
}>(({ product, productQuantity, cartItemReference, enableAutoFill, onAutoFillQuantity }) => {
    return (
        <GiftsSelectorWidgetContents
            product={product}
            productQuantity={productQuantity}
            cartItemReference={cartItemReference}
            enableAutoFill={enableAutoFill}
            onAutoFillQuantity={onAutoFillQuantity}
        />
    );
});

MemoizedContent.displayName = 'MemoizedContent';

/**
 * Inner GiftsSelectorWidget component - wrapped by withBottomSheets HOC
 * This is a headless component that renders null and exposes openBottomSheet() via ref
 */
const GiftsSelectorWidgetInner = observer(
    forwardRef(function GiftsSelectorWidgetInnerImpl(
        props: GiftsSelectorWidgetProps & AppBottomSheetInjectedProps,
        ref: ForwardedRef<GiftsSelectorWidgetRef>
    ) {
        const {
            product,
            productQuantity: productQuantityProp,
            cartItemReference,
            onClose,
            onConfirm: onConfirmInput,
            bottomSheet,
        } = props;

        const { sortedGiftsDataList, addGiftToCartItemReferenceLogic } = useProductGiftHook({
            product,
            fetchGiftsFromCache: false,
        });

        // Determine if we're in cart context (editing existing cart item gifts)
        const isCartContext = !!cartItemReference;

        // Determine product quantity based on context:
        // - Cart context: use cartItemReference.qty (current cart item quantity)
        // - PDP context: use productQuantityProp if provided, otherwise use cartItemReferenceStore quantity
        const draftQuantity = cartItemReferenceStoreAPI?.state?.getQuantityStepperObservable().get() ?? 1;
        const counterValue = isCartContext ? (cartItemReference?.qty ?? 1) : (productQuantityProp ?? draftQuantity);
        const { t } = useTranslation('flash-sale');
        /**
         * Intelligent Hook - sessionId as KEY
         * - PDP: use existing session from product-gift component
         * - Cart: use cart item reference based session
         */
        const pdpGiftHook = useGiftSelection();
        const pdpSessionId = pdpGiftHook.getActiveSessionId(`${product?.id}`, 'product') ?? '';

        const cartGiftSelection = useCartItemGiftSelection({
            cartItemReference: cartItemReference ?? {
                cartItemReferenceId: '',
                productId: product?.id ?? 0,
                qty: counterValue,
                logics: [],
                isSelected: false,
            },
            availableGifts: sortedGiftsDataList,
            sessionId: pdpSessionId,
        });

        // Select the appropriate gift selection based on context
        const selectedGiftsObservable = isCartContext
            ? cartGiftSelection.store$?.selectedGifts
            : pdpGiftHook.getStoreObservable(pdpSessionId)?.selectedGifts;

        // Auto-fill handler: update quantity stepper and session
        const handleAutoFillQuantity = useCallback(
            (newQuantity: number) => {
                // PDP context: update the cartItemReferenceStore quantity stepper
                cartItemReferenceStoreAPI.state.getQuantityStepperObservable().set(newQuantity);

                // Update session productQuantity
                if (pdpSessionId) {
                    giftSelectionStoreAPI.actions.updateSession(pdpSessionId, {
                        productQuantity: newQuantity,
                    });
                }
            },
            [pdpSessionId]
        );

        // Memoize close handler with stable dependencies
        const handleBottomSheetClose = useCallback(() => {
            bottomSheet.close();
            onClose?.();
        }, [bottomSheet, onClose]);

        const handleClearSession = useCallback(() => {
            if (isCartContext) {
                return;
            }
            // Only clear selected gift in pdp context
            pdpGiftHook.clearSession(pdpSessionId);
        }, [isCartContext, pdpGiftHook, pdpSessionId]);

        // Memoize confirm handler with stable dependencies
        const handleConfirm = useCallback(async () => {
            // Guard: Ensure selectedGiftsObservable exists before proceeding
            if (!selectedGiftsObservable) {
                console.warn('[GiftsSelectorWidget] Cannot confirm: selectedGiftsObservable is undefined');
                bottomSheet.close();
                return;
            }

            addGiftToCartItemReferenceLogic({ selectedGifts$: selectedGiftsObservable, sessionId: pdpSessionId });
            await onConfirmInput?.();
            handleClearSession();
            bottomSheet.close();
        }, [
            addGiftToCartItemReferenceLogic,
            handleClearSession,
            bottomSheet,
            onConfirmInput,
            pdpSessionId,
            selectedGiftsObservable,
        ]);

        // Memoize extra params (static configuration)
        const extraParams = useMemo<AppBottomSheetHeaderProps>(
            () => ({
                title: t('text_gift_choose_accept-change', 'Chọn quà tặng'),
                titleProps: { className: styles.titleBottomSheet },
            }),
            [t]
        );

        // Memoize footer with stable callbacks
        const footerElement = useMemo(
            () => <MemoizedFooter onBottomSheetClose={handleBottomSheetClose} onConfirm={handleConfirm} />,
            [handleBottomSheetClose, handleConfirm]
        );

        // Memoize bottom sheet content with stable dependencies
        const bottomSheetContent = useMemo(
            () => (
                <MemoizedContent
                    product={product}
                    productQuantity={counterValue}
                    cartItemReference={cartItemReference}
                    enableAutoFill={true}
                    onAutoFillQuantity={handleAutoFillQuantity}
                />
            ),
            [product, counterValue, cartItemReference, handleAutoFillQuantity]
        );

        // Memoize open function with stable dependencies
        const handleOpenBottomSheet = useCallback(() => {
            bottomSheet.inject(bottomSheetContent, extraParams, undefined, { component: footerElement });
            bottomSheet.show();
        }, [bottomSheet, bottomSheetContent, extraParams, footerElement]);

        // Expose API to parent via ref with stable reference
        useImperativeHandle(
            ref,
            () => ({
                openBottomSheet: handleOpenBottomSheet,
            }),
            [handleOpenBottomSheet]
        );

        // Headless component - no UI rendering, only provides bottom sheet functionality via ref
        return null;
    })
);

GiftsSelectorWidgetInner.displayName = 'GiftsSelectorWidgetInner';

// Export component with forwardRef and HOC - matching mobile pattern
export const GiftsSelectorWidget = withAppBottomSheet(GiftsSelectorWidgetInner);
